import React, { useState, useRef, useContext } from 'react';
import { Button, Dropdown } from 'components/ui';
import { HiOutlinePaperAirplane, HiOutlinePhotograph, HiOutlinePlus } from 'react-icons/hi';
import { BsStars, BsImage } from 'react-icons/bs';
import { motion, AnimatePresence } from 'framer-motion';
import ImagePreview from './ImagePreview';
import ImageChatModelSelector from './ImageChatModelSelector';
import AIPriceBadge from 'components/shared/AIPriceBadge';
import { ImageChatContext } from 'contexts/ImageChatContext';
import { AIContext, aiSizeOptions } from 'contexts/AIContext';

const ImageChatInput = ({
    selectedImages,
    onRemoveImage,
    onSendMessage, 
    onOpenImageSelector,
    placeholder,
    isGenerating,
    maxImages,
    aspectRatio,
    onAspectRatioChange
}) => {
    const [message, setMessage] = useState('');
    const textareaRef = useRef(null);
    const { editAIModel: currentModelObj, selectedModel, setSelectedModel } = useContext(ImageChatContext);
    const { aiImageModels } = useContext(AIContext);

    // Get the price of the current model
    const modelPrice = currentModelObj?.price || 0;

    const availableSizes = currentModelObj?.availableSizes || ["1:1", "16:9", "9:16", "4:3", "3:4"];
    
    // Filter aspect ratio options based on current model's supported sizes
    const aspectRatioOptions = aiSizeOptions.filter(option => 
        availableSizes.includes(option.id)
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        if (message.trim() && selectedImages.length > 0 && !isGenerating) {
            onSendMessage(message);
            setMessage('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const handleTextareaChange = (e) => {
        setMessage(e.target.value);
        
        // Auto-resize textarea
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = Math.min(textarea.scrollHeight, 48) + 'px';
        }
    };

    const canSend = message.trim() && selectedImages.length > 0 && !isGenerating;
    const canAddMore = selectedImages.length < maxImages;

    return (
        <div className="relative border-t border-gray-200/50 dark:border-gray-700/50 backdrop-blur-xl">
            {/* Animated Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-pink-500/5 via-transparent to-transparent dark:from-pink-500/10 pointer-events-none"></div>
            
            <div className="relative bg-white/80 dark:bg-gray-900/80">
                {/* Image Previews with Model Selector Row */}
                <div className="p-3 lg:p-4 !pb-2">
                    <div className="flex items-center gap-2 justify-between">
                        {/* Left side - Image previews and add button */}
                        <div className="flex flex-wrap gap-2 items-center flex-1 min-w-0">
                            <AnimatePresence mode="popLayout">
                                {selectedImages.map((image, index) => (
                                    <motion.div
                                        key={image.id}
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        exit={{ scale: 0, opacity: 0 }}
                                        transition={{ duration: 0.2, delay: index * 0.05 }}
                                    >
                                        <ImagePreview
                                            image={image}
                                            onRemove={() => onRemoveImage(image.id)}
                                        />
                                    </motion.div>
                                ))}
                                <motion.button
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0, opacity: 0 }}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    type="button"
                                    onClick={onOpenImageSelector}
                                    disabled={isGenerating}
                                    className="w-14 h-14 border-2 border-dashed border-pink-300 dark:border-pink-600 rounded-xl flex items-center justify-center hover:border-pink-500 dark:hover:border-pink-400 hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
                                    title={selectedImages.length > 0 ? `Add more images (${selectedImages.length}/${maxImages})` : 'Add images'}
                                >
                                    <HiOutlinePlus className="w-5 h-5 text-pink-400 dark:text-pink-500 group-hover:text-pink-600 dark:group-hover:text-pink-400 transition-colors" />
                                </motion.button>
                            </AnimatePresence>
                        </div>

                        {/* Right side - Model and Aspect Ratio Controls */}
                        <div className="flex items-center gap-2 flex-shrink-0 -mb-2">
                            {/* Model Selector */}
                            <div className="w-auto min-w-[120px] max-w-[200px]">
                                <ImageChatModelSelector
                                    selectedModel={selectedModel}
                                    onModelChange={setSelectedModel}
                                    compact={true}
                                />
                            </div>

                            {/* Aspect Ratio Dropdown */}
                            {aspectRatioOptions.length > 1 && (
                                <Dropdown
                                    renderTitle={
                                        <motion.div 
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            className='flex items-center gap-2 px-3 py-2 bg-gradient-to-br from-white/80 to-white/60 dark:from-gray-800/80 dark:to-gray-900/80 border border-pink-200/50 dark:border-pink-500/20 rounded-xl backdrop-blur-xl shadow-lg shadow-pink-500/5 hover:shadow-pink-500/10 hover:border-pink-300/60 dark:hover:border-pink-500/30 transition-all duration-300'
                                        >
                                            <span className='text-xs font-semibold text-gray-800 dark:text-gray-200'>{aspectRatio}</span>
                                        </motion.div>
                                    }
                                    placement="top-end"
                                    onSelect={(value) => onAspectRatioChange?.(value)}
                                    disabled={isGenerating}
                                    menuClass="!bg-white/95 dark:!bg-gray-900/95 backdrop-blur-xl !border-pink-200/50 dark:!border-pink-500/20 !shadow-2xl"
                                >
                                    {aspectRatioOptions.map((option) => (
                                        <Dropdown.Item 
                                            key={option.id} 
                                            eventKey={option.id}
                                            className={`
                                                hover:!bg-pink-50/80 dark:hover:!bg-pink-900/20
                                                ${aspectRatio === option.id ? 'bg-pink-100/50 dark:bg-pink-900/30' : ''}
                                            `}
                                        >
                                            <div className="py-2 px-3 text-sm font-medium text-gray-800 dark:text-gray-200">
                                                {option.label}
                                            </div>
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown>
                            )}
                        </div>
                    </div>
                </div>

                {/* Input Area */}
                <div className="p-3 lg:p-4 !pt-0">
                    <form onSubmit={handleSubmit}>
                        <div className="flex items-center gap-2">
                            {/* Text Input */}
                            <div className="flex-1 relative">
                                <textarea
                                    ref={textareaRef}
                                    value={message}
                                    onChange={handleTextareaChange}
                                    onKeyDown={handleKeyDown}
                                    placeholder={placeholder}
                                    disabled={isGenerating}
                                    rows={1}
                                    className="w-full px-4 py-3 bg-gradient-to-br from-gray-50/80 to-white/80 dark:from-gray-800/80 dark:to-gray-900/80 border-2 border-pink-200/50 dark:border-pink-700/30 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-pink-500/50 dark:focus:ring-pink-400/50 focus:border-pink-300 dark:focus:border-pink-500 transition-all text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 min-h-[48px] max-h-[120px] backdrop-blur-xl shadow-lg"
                                    style={{ height: '48px' }}
                                />
                            </div>

                            {/* Send Button with Price Badge */}
                            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="flex-shrink-0">
                                <Button
                                    type="submit"
                                    variant="solid"
                                    size="lg"
                                    className={`
                                        p-3 rounded-2xl transition-all h-[48px] min-w-[48px] flex items-center justify-center gap-2
                                        ${canSend 
                                            ? '!bg-gradient-to-r !from-pink-500 !to-orange-600 hover:!from-pink-600 hover:!to-orange-700 !border-0 !text-white shadow-lg shadow-pink-500/30 hover:shadow-pink-500/40' 
                                            : '!bg-gray-200 dark:!bg-gray-700 !border-0 !text-gray-400 dark:!text-gray-500'
                                        }
                                    `}
                                    disabled={!canSend}
                                    loading={isGenerating}
                                >
                                    {isGenerating ? (
                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                        >
                                            <BsStars className="w-5 h-5" />
                                        </motion.div>
                                    ) : (
                                        <>
                                            <HiOutlinePaperAirplane className="w-5 h-5" />
                                            {canSend && (
                                                <AIPriceBadge
                                                    price={modelPrice}
                                                    quantity={1}
                                                    priceType="generation"
                                                    size="sm"
                                                    variant="default"
                                                    className="!bg-white/20 !text-white !border-white/30 hidden lg:inline-flex"
                                                />
                                            )}
                                        </>
                                    )}
                                </Button>
                            </motion.div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ImageChatInput; 